import {
  hasOwnProperty,
  hasTypedProperty,
  isRecordOf,
} from '@one-click-lca/3d-buildings-shared/utils';

export const TOOLS2D: {[key: string]: symbol} = {
  SPLIT: Symbol('Split'),
  MERGE: Symbol('Merge'),
  DELETE: Symbol('Delete'),
  SELECT: Symbol('Select'),
  RESET: Symbol('Reset'),
  CIRCLE_SELECT: Symbol('CircleSelect'),
  POLYGON_SELECT: Symbol('PolygonSelect'),
  SELECTION_RESET: Symbol('SelectionReset'),
  TOGGLE_LIMIT: Symbol('ToggleLimit'),
};

export const DOM_ID = {
  mainView: 'mainView',
  notificationContainer: 'notificationContainer',
  updatePerimeterButton: 'updatePerimeterButton',
  projectTabs: 'projectTabs',
  versionSelector: 'versionSelector',
  mapViewSwitchButtons: 'mapViewSwitchButtons',
  buildButton2D: 'buildButton2D',
  buildButton3D: 'buildButton3D',
  autoBuildrButton: 'autoBuildrButton',
  buildButtons: 'buildButtons',
  pluButton: 'pluButton',
  marketTab: 'marketTab',
  switchButton: 'switchButton',
  comparableTable: 'pluButcomparableTableton',
  headerExtraButtons: 'headerExtraButtons',
  versionExtraButtons: 'versionExtraButtons',
  reinitializeTuto: 'reinitializeTuto',
  geometryEditionControls: 'geometryEditionControls',
  geometrySelectionSaveButton: 'geometrySelectionSaveButton',
  pluAutoNotificationsContainer: 'pluAutoNotificationsContainer',
};

export const MAP_CONTENT_MODE = {
  PROJECTS: Symbol('Projects'),
  TRANSACTIONS: Symbol('Transactions'),
  MUTABILITIES: Symbol('Mutabilities'),
  PROJECT: Symbol('Project'),
  SEARCH: Symbol('Search'),
};

//serialized in the project, do not change the existing values
export enum ParkingStrategy {
  FillBuildingFootprint = 'fillBuildingFootprint',
  EnlargeParking = 'enlargeParking',
  FillParcel = 'fillParcel',
}

export enum RoofSpecification {
  hasSlopedRoof = 'hasSlopedRoof',
  hasFlatRoof = 'hasFlatRoof',
  unspecified = 'unspecified',
}

export enum TYPOLOGY {
  MULTIFAMILY = 'multifamily',
  ROW = 'row',
  INTERMEDIATE = 'intermediate',
  OFFICES = 'offices',
  COMMERCIAL = 'commercial',
  CUSTOM = 'custom',
  NOT_SET = 'not_set',
}

export type BaseRatio = {
  isDefault: boolean;
  h_etage: number;
  shon_shob: number;
  rho_plan: number;
  ratio_sp_shob_ground_floor: number;
  ratio_sp_shob: number;
  sh_moyenne: number;
  rho_constru_ll: number;
  rho_constru_ls: number;
  rho_constru_commerce: number;
  rho_constru_bureau: number;
  hasExtPark: boolean;
  subParkLevelCount: number;
  top_parking_sousterrain: boolean;
  rho_tac: number;
  rho_tad: number;
  rho_tar: number;
  rho_assiette_taxe: number;
  rho_assiette_taxe_hors_IDF: number;
  prix_neuf_libre: number;
  prix_neuf_social: number;
  prix_neuf_commerce: number;
  prix_neuf_bureau: number;
  subventions: number;
  rho_frais_fonciers: number;
  rho_demol: number;
  rho_depol: number;
  rho_viab: number;
  soultePrice: number;
  miscellaneousPrice: number;
  legalTotal: number;
  c_foncier: number;
  rho_park_st: number;
  rho_park_surf: number;
  rho_park: number;
  rho_moe: number;
  rho_autre_etude: number;
  rho_gestion: number;
  rho_commercialisation: number;
  rho_juridique: number;
  rho_taxe_autre: number;
  rho_assurance: number;
  rho_aleas: number;
  rho_finance_charges: number;
  rho_marge: number;
  default_part_ll: number;
  default_part_ls: number;
  default_part_midRangeHousing: number;
  default_part_managedHousing: number;
  default_top_commerce_rdc: boolean;
  roofSpecification: RoofSpecification;
  roofOrAttic: boolean;
  max_building_depth: number | null;
  min_building_depth: number | null;
  max_facade_length: number | null;
  min_facade_length: number | null;
  max_building_width: number | null;
  min_building_width: number | null;
  min_building_sdp: number | null;
  max_building_sdp: number | null;
  min_building_gfa: number | null;
  max_building_gfa: number | null;
  min_buildings: number;
  max_buildings: number;
  max_building_levels: number | null;
  max_building_height: number | null;
  min_building_height: number | null;
  row_houses_garden_depth: number;
  is_dependency: boolean;
  salesOfficeAllTaxPrice: number;
  salesOfficeInvestorAllTaxPrice: number;
  transferPrice: number;
  intermediaryCommissionPrice: number;
  connectingCost: number;
  salesOfficeTax: number;
  salesOfficeInvestorTax: number;
  dationTax: number;
  dationArea: number;
  socialHousingTax: number;
  intermediateHousingTax: number;
  intermediateHousingPreTaxPrice: number;
  managedHousingTax: number;
  managedHousingPreTaxPrice: number;
  managedHousingArea: number;
  blockManagedHousingTax: number;
  blockManagedHousingPreTaxPrice: number;
  officeTax: number;
  officePreTaxPrice: number;
  shopTax: number;
  shopPreTaxPrice: number;
  mutualAgriculturalLandGroupingPrice: number;
  blocChargesPrice: number;
  otherChargesPrice: number;
  salesAidPrice: number;
  internalManagementPrice: number;
  salesOfficeMarketingPrice: number;
  salesOfficeInvestorMarketingPrice: number;
  advertisingPrice: number;
  taxesPrice: number;
  risksPrice: number;
  insurancePrice: number;
  numberLevelsOfParking: number;
  eBusinessPrice: number;
  extraParkingPrice: number;
  groundParkingPrice: number;
  hasImprovementTax: boolean;
  parkingStrategy: ParkingStrategy;
  typology: TYPOLOGY;
};

const DEFAULT_GLOBAL_RATIOS: BaseRatio = {
  isDefault: true,
  h_etage: 2.85,
  shon_shob: 0.85,
  rho_plan: 0.93,
  ratio_sp_shob_ground_floor: 0.7,
  ratio_sp_shob: 0.83,
  sh_moyenne: 60,
  rho_constru_ll: 1600,
  rho_constru_ls: 1500,
  rho_constru_commerce: 1000,
  rho_constru_bureau: 1400,
  hasExtPark: false,
  subParkLevelCount: 5,
  top_parking_sousterrain: true,
  rho_tac: 0.05,
  rho_tad: 0.02,
  rho_tar: 0.01,
  rho_assiette_taxe: 1004,
  rho_assiette_taxe_hors_IDF: 886,
  prix_neuf_libre: 4000,
  prix_neuf_social: 3000,
  prix_neuf_commerce: 3000,
  prix_neuf_bureau: 4000,
  subventions: 0,
  rho_frais_fonciers: 0.02,
  rho_demol: 80,
  rho_depol: 50,
  rho_viab: 50,
  soultePrice: 0,
  miscellaneousPrice: 0,
  legalTotal: 0,
  c_foncier: 0,
  rho_park_st: 18000,
  rho_park_surf: 2000,
  rho_park: 18000,
  rho_moe: 0.08,
  rho_autre_etude: 0.03,
  rho_gestion: 0.05,
  rho_commercialisation: 0.05,
  rho_juridique: 0.0055,
  rho_taxe_autre: 0,
  rho_assurance: 0.035,
  rho_aleas: 0.03,
  rho_finance_charges: 0.01,
  rho_marge: 0.08,
  default_part_ll: 0.7,
  default_part_ls: 0.3,
  default_part_midRangeHousing: 0.0,
  default_part_managedHousing: 0.0,
  default_top_commerce_rdc: false,
  roofSpecification: RoofSpecification.unspecified,
  roofOrAttic: false,
  max_building_depth: 50,
  min_building_depth: null,
  max_facade_length: null,
  min_facade_length: null,
  max_building_width: null,
  min_building_width: null,
  min_building_sdp: null,
  max_building_sdp: null,
  min_building_gfa: null,
  max_building_gfa: null,
  min_buildings: 1, //integer
  max_buildings: 30, //integer
  max_building_levels: null, //integer
  max_building_height: null,
  min_building_height: null,
  row_houses_garden_depth: 5,
  is_dependency: false,
  salesOfficeAllTaxPrice: 5500,
  salesOfficeInvestorAllTaxPrice: 5500,
  transferPrice: 1.38,
  intermediaryCommissionPrice: 8,
  connectingCost: 0,
  salesOfficeTax: 20,
  salesOfficeInvestorTax: 20,
  dationTax: 20,
  dationArea: 0,
  socialHousingTax: 10,
  intermediateHousingTax: 10,
  intermediateHousingPreTaxPrice: 3800,
  managedHousingTax: 20,
  managedHousingPreTaxPrice: 4000,
  managedHousingArea: 0,
  blockManagedHousingTax: 20,
  blockManagedHousingPreTaxPrice: 4200,
  officeTax: 20,
  officePreTaxPrice: 4500,
  shopTax: 20,
  shopPreTaxPrice: 3800,
  mutualAgriculturalLandGroupingPrice: 1,
  blocChargesPrice: 0.5,
  otherChargesPrice: 1,
  salesAidPrice: 0,
  internalManagementPrice: 5,
  salesOfficeMarketingPrice: 5,
  salesOfficeInvestorMarketingPrice: 5,
  advertisingPrice: 1.5,
  taxesPrice: 0.29,
  risksPrice: 3,
  insurancePrice: 1.4,
  numberLevelsOfParking: 1,
  eBusinessPrice: 0,
  extraParkingPrice: 0,
  groundParkingPrice: 7000,
  hasImprovementTax: true,
  parkingStrategy: ParkingStrategy.EnlargeParking,
  typology: TYPOLOGY.CUSTOM,
};

export function isBaseRatios(ratios: unknown): ratios is BaseRatio {
  return hasTypedProperty(
    ratios,
    'h_etage',
    (n: unknown): n is number => typeof n === 'number',
  );
}
export type BuildingRatio = Partial<BaseRatio> & {
  part_ll?: number;
  part_ls?: number;
  hauteur_rdc?: number;
  top_commerce_rdc: boolean;
  part_managedHousing?: number;
  part_midRangeHousing?: number;
};
export function isBuildingRatio(ratios: unknown): ratios is BuildingRatio {
  return hasTypedProperty(
    ratios,
    'top_commerce_rdc',
    (x: unknown): x is boolean => typeof x === 'boolean',
  );
}
export type BuildingsRatios = {
  global: BaseRatio;
  buildings: Record<string, BuildingRatio>;
};
export function isBuildingsRatios(ratios: unknown): ratios is BuildingsRatios {
  return (
    hasTypedProperty(ratios, 'global', isBaseRatios) &&
    hasOwnProperty(ratios, 'buildings') &&
    isRecordOf(ratios.buildings, isBuildingRatio)
  );
}
export type CompleteRatios = BuildingsRatios & {
  tenements: Record<string, Partial<BaseRatio>>;
};
export function isCompleteRatios(ratios: unknown): ratios is CompleteRatios {
  return isBuildingsRatios(ratios) && hasOwnProperty(ratios, 'tenements');
}

export const DEFAULT_RATIOS: CompleteRatios = {
  global: DEFAULT_GLOBAL_RATIOS,
  buildings: {},
  tenements: {},
};

export const MAX_NUMBER_OF_PERSONS_DISPLAYED = 2;

export const PUBLIC_WEBSITE_HOSTNAME = 'www.buildrz.io';

export const WEB_APP_URL = 'https://app.buildrz.io/';

export const ASCENDING_SORT = 'asc'; // As used in Lodash
export const DESCENDING_SORT = 'desc';

export const COMMENT_TAG = 'COMMENTAIRE: ';

export const FAISA_TOOLTIP_SELECTOR = 'best-faisa-tooltip';
